import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FiArrowRight, FiCheckCircle, FiZap, FiCpu, FiDatabase,
  FiUsers, FiTrendingUp, FiShield, FiTarget, FiBarChart2,
  FiMessageSquare, FiStar, FiPhone, FiMail, FiTerminal
} from 'react-icons/fi';
import Footer from '../../components/Footer';

const RAINBOW = [
  '#ff0040','#ff4000','#ff8000','#ffc000',
  '#ffff00','#80ff00','#00ff80','#00ffff',
  '#0080ff','#4000ff','#8000ff','#ff00ff',
];

function rainbowText(text) {
  const chars = text.split('');
  const vis = chars.filter(c => c !== ' ').length;
  let ci = 0;
  return chars.map((ch, i) => {
    if (ch === ' ') return <span key={i}>&nbsp;</span>;
    const color = RAINBOW[Math.floor((ci / vis) * RAINBOW.length) % RAINBOW.length];
    ci++;
    return <span key={i} style={{ color }}>{ch}</span>;
  });
}

function TerminalTyper({ text, delay = 600 }) {
  const [shown, setShown] = useState(0);
  const [done, setDone] = useState(false);
  useEffect(() => {
    const t0 = setTimeout(() => {
      const iv = setInterval(() => {
        setShown(s => {
          if (s >= text.length) { clearInterval(iv); setDone(true); return s; }
          return s + 1;
        });
      }, 28);
      return () => clearInterval(iv);
    }, delay);
    return () => clearTimeout(t0);
  }, [text, delay]);
  return (
    <span>
      {text.slice(0, shown)}
      <span className={`con-cursor ${done ? 'blink' : ''}`}>_</span>
    </span>
  );
}

const SERVICES = [
  {
    slug: 'ai-automation',
    icon: <FiCpu />,
    title: 'AI & Automation',
    tagline: 'Deploy intelligent automation that eliminates manual work and scales with your business.',
    bullets: [
      'Custom AI agent development & deployment',
      'LLM integration (GPT, Claude, Gemini, open-source)',
      'Workflow automation replacing Zapier/Make',
      'Internal copilot & assistant development',
    ],
    color: '#0080ff',
    tag: 'AI',
  },
  {
    slug: 'software-engineering',
    icon: <FiZap />,
    title: 'Software Engineering',
    tagline: 'Full-stack teams embedded in your workflow — shipping production code from week one.',
    bullets: [
      'React, Node, Python, cloud-native stacks',
      'API design & microservices architecture',
      'Legacy system modernization',
      'DevOps, CI/CD pipeline optimization',
    ],
    color: '#8000ff',
    tag: 'ENG',
  },
  {
    slug: 'data-analytics',
    icon: <FiDatabase />,
    title: 'Data & Analytics',
    tagline: 'Transform raw data into competitive intelligence with modern pipelines and ML models.',
    bullets: [
      'Data warehouse design & ETL pipelines',
      'Business intelligence dashboards',
      'Predictive analytics & ML models',
      'Real-time streaming architectures',
    ],
    color: '#00ff80',
    tag: 'DATA',
  },
  {
    slug: 'crm-operations',
    icon: <FiUsers />,
    title: 'CRM & Operations',
    tagline: 'Optimize customer operations with AI-enhanced CRM systems and intelligent workflows.',
    bullets: [
      'CRM implementation & customization',
      'Sales pipeline automation',
      'Customer data platform integration',
      'AI-powered lead scoring & routing',
    ],
    color: '#ffc000',
    tag: 'OPS',
  },
];

const RESULTS = [
  { metric: '74%', label: 'Avg. reduction in manual workflow time', color: '#ff0040' },
  { metric: '3–6 wk', label: 'Time to first production deployment', color: '#00ffff' },
  { metric: '35%', label: 'Avg. productivity increase for clients', color: '#00ff80' },
  { metric: '96.3%', label: 'Accuracy on document intel systems', color: '#8000ff' },
];

const TESTIMONIALS = [
  {
    quote: 'Vector Labs replaced our entire Zapier stack with custom AI agents in 4 weeks. The reliability improvement alone paid for the engagement in the first month.',
    author: 'VP Engineering',
    company: 'Series B SaaS Company',
  },
  {
    quote: 'Their team embedded with ours and shipped a production RAG system processing 50K docs/month. No other vendor came close to that velocity.',
    author: 'CTO',
    company: 'Legal Tech Startup',
  },
  {
    quote: 'We went from zero AI capability to a fully deployed internal copilot in 6 weeks. Our support team handles 3x the tickets now.',
    author: 'Head of Operations',
    company: 'Mid-Market Fintech',
  },
];

const PROCESS = [
  { step: '01', title: 'Discovery Call', desc: 'Free 30-min consultation. We map your pain points, tech stack, and goals.', color: '#0080ff' },
  { step: '02', title: 'Technical Audit', desc: 'Our engineers assess your systems and deliver a prioritized implementation roadmap.', color: '#8000ff' },
  { step: '03', title: 'Rapid Build', desc: 'Embedded engineering team ships production-ready features in 2–4 week sprints.', color: '#00ff80' },
  { step: '04', title: 'Scale & Support', desc: 'Ongoing optimization, monitoring, and knowledge transfer to your internal team.', color: '#ffc000' },
];

const FAQ = [
  { q: 'What size companies do you work with?', a: 'We serve startups (seed through Series C), mid-market companies, and enterprise organizations. Our engagements range from 2-person rapid builds to 8+ person embedded teams.' },
  { q: 'How quickly can you start?', a: 'Most engagements begin within 1–2 weeks of signing. We maintain bench capacity specifically for rapid-start projects.' },
  { q: 'Do you work with our existing tech stack?', a: 'Yes. We integrate with whatever you\'re running — AWS, GCP, Azure, on-prem, hybrid. We\'re stack-agnostic and pragmatic about technology choices.' },
  { q: 'What does pricing look like?', a: 'Engagements start at $15K/month for a focused 2-person team. Enterprise and longer-term contracts include volume discounts. Every engagement starts with a free discovery call.' },
  { q: 'Can you work under NDA?', a: 'Absolutely. We execute NDAs and MSAs before any technical discussions. All client work is confidential by default.' },
  { q: 'Do you offer staff augmentation?', a: 'Yes. We can embed individual engineers or full squads into your team, working in your repos, your tools, your standups.' },
];

const INDUSTRIES = [
  'Software & SaaS', 'Artificial Intelligence', 'Financial Services', 'Healthcare & Biotech',
  'E-Commerce & Retail', 'Legal & Compliance', 'Manufacturing', 'Real Estate & PropTech',
  'Education & EdTech', 'Logistics & Supply Chain', 'Media & Publishing', 'Government & Defense',
];

export default function ConsultingPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({
    name: '', email: '', company: '', size: '', service: '', message: ''
  });

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Thank you! We\'ll reach out within 24 hours.');
    setFormData({ name: '', email: '', company: '', size: '', service: '', message: '' });
  };

  return (
    <div className="con-page">
      {/* Hero */}
      <section className="con-hero">
        <div className="con-hero-inner">
          <div className="con-badge"><FiTerminal /> consulting</div>
          <h1 className="con-hero-title">{rainbowText('CONSULTING')}</h1>
          <p className="con-hero-tagline">B2B AI & Engineering Consulting</p>
          <p className="con-hero-sub">
            Elite engineering teams that build production AI systems,
            automate workflows, and modernize software — so your team can focus on growth.
          </p>

          <div className="con-cli">
            <div className="con-cli-header">
              <span className="ph-dot" style={{background:'#ff5f57'}}></span>
              <span className="ph-dot" style={{background:'#febc2e'}}></span>
              <span className="ph-dot" style={{background:'#28c840'}}></span>
              <span className="con-cli-title">vector-labs ~ consulting</span>
            </div>
            <div className="con-cli-body">
              <div className="con-cli-line">
                <span className="con-prompt">$</span>
                <TerminalTyper text='vector-labs consulting --status' delay={800} />
              </div>
              <div className="con-cli-output">
                <br/>
                <span style={{color:'rgba(255,255,255,0.35)'}}>  METRIC              VALUE</span><br/>
                <span style={{color:'#00ff80'}}>  deployments</span>         <span style={{color:'rgba(255,255,255,0.6)'}}>40+ production</span><br/>
                <span style={{color:'#00ffff'}}>  avg_delivery</span>       <span style={{color:'rgba(255,255,255,0.6)'}}>3–6 weeks</span><br/>
                <span style={{color:'#8000ff'}}>  location</span>            <span style={{color:'rgba(255,255,255,0.6)'}}>Charlotte, NC — global</span><br/>
                <span style={{color:'#ffc000'}}>  status</span>              <span style={{color:'#00ff80'}}>● accepting clients</span><br/>
              </div>
            </div>
          </div>

          <div className="con-hero-actions">
            <a href="#contact" className="con-btn-primary">
              Get a Free Consultation <FiArrowRight />
            </a>
            <a href="#services" className="con-btn-ghost">
              View Services
            </a>
          </div>

          <div className="con-proof">
            <div className="con-proof-item"><FiCheckCircle /> 40+ production deployments</div>
            <div className="con-proof-item"><FiCheckCircle /> 3–6 week average delivery</div>
            <div className="con-proof-item"><FiCheckCircle /> Charlotte, NC — serving clients globally</div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="con-services" id="services">
        <div className="con-container">
          <div className="con-section-head">
            <span className="con-overline">What We Do</span>
            <h2>End-to-End AI & Software Consulting</h2>
            <p>From strategy to production — the full stack of modern software and AI deployment.</p>
          </div>
          <div className="con-services-grid">
            {SERVICES.map((svc) => (
              <Link to={`/consulting/${svc.slug}`} key={svc.slug} className="con-svc-card" style={{'--svc-color': svc.color}}>
                <div className="con-svc-top">
                  <div className="con-svc-icon">{svc.icon}</div>
                  <span className="con-svc-tag">{svc.tag}</span>
                </div>
                <h3>{svc.title}</h3>
                <p className="con-svc-tagline">{svc.tagline}</p>
                <ul>
                  {svc.bullets.map((b) => (
                    <li key={b}><FiCheckCircle /> {b}</li>
                  ))}
                </ul>
                <span className="con-svc-link">
                  Learn more <FiArrowRight />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="con-results">
        <div className="con-container">
          <div className="con-section-head">
            <span className="con-overline">Proven Results</span>
            <h2>Numbers That Speak</h2>
          </div>
          <div className="con-results-grid">
            {RESULTS.map((r) => (
              <div className="con-result-card" key={r.label}>
                <span className="con-result-metric" style={{color: r.color}}>{r.metric}</span>
                <span className="con-result-label">{r.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="con-industries">
        <div className="con-container">
          <div className="con-section-head">
            <span className="con-overline">Industries We Serve</span>
            <h2>Deep Expertise Across Verticals</h2>
            <p>Whether you're a 10-person startup or a Fortune 500, we bring sector-specific knowledge.</p>
          </div>
          <div className="con-industries-grid">
            {INDUSTRIES.map((ind) => (
              <div className="con-industry-chip" key={ind}>
                <FiTarget /> {ind}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="con-process">
        <div className="con-container">
          <div className="con-section-head">
            <span className="con-overline">How We Work</span>
            <h2>Discovery to Deployment in Weeks</h2>
          </div>
          <div className="con-process-grid">
            {PROCESS.map((p) => (
              <div className="con-process-card" key={p.step}>
                <span className="con-process-step" style={{color: p.color}}>{p.step}</span>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="con-testimonials">
        <div className="con-container">
          <div className="con-section-head">
            <span className="con-overline">Client Results</span>
            <h2>What Our Clients Say</h2>
          </div>
          <div className="con-testimonials-grid">
            {TESTIMONIALS.map((t, i) => (
              <div className="con-testimonial-card" key={i}>
                <div className="con-stars">
                  {[...Array(5)].map((_, j) => <FiStar key={j} />)}
                </div>
                <p className="con-quote">"{t.quote}"</p>
                <div className="con-author">
                  <strong>{t.author}</strong>
                  <span>{t.company}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="con-faq">
        <div className="con-container">
          <div className="con-section-head">
            <span className="con-overline">FAQ</span>
            <h2>Common Questions</h2>
          </div>
          <div className="con-faq-list">
            {FAQ.map((item, i) => (
              <div className={`con-faq-item ${openFaq === i ? 'open' : ''}`} key={i}>
                <button className="con-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  {item.q}
                  <span className="con-faq-toggle">{openFaq === i ? '−' : '+'}</span>
                </button>
                {openFaq === i && <p className="con-faq-a">{item.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact / CTA */}
      <section className="con-contact" id="contact">
        <div className="con-container">
          <div className="con-contact-grid">
            <div className="con-contact-info">
              <span className="con-overline">Let's Talk</span>
              <h2>Ready to Move Faster?</h2>
              <p>
                Schedule a free 30-minute discovery call. We'll map your challenges, assess fit,
                and outline a plan — no obligation, no sales pressure.
              </p>
              <div className="con-contact-details">
                <div className="con-contact-item"><FiPhone /> (704) 555-0142</div>
                <div className="con-contact-item"><FiMail /> consulting@vectorlabsnc.com</div>
                <div className="con-contact-item"><FiTarget /> Charlotte, NC — Remote & On-Site</div>
              </div>
              <div className="con-contact-badges">
                <div className="con-badge-item"><FiShield /> NDA-Ready</div>
                <div className="con-badge-item"><FiTrendingUp /> SOC 2 Compliant</div>
                <div className="con-badge-item"><FiBarChart2 /> Free Assessment</div>
              </div>
            </div>
            <form className="con-form" onSubmit={handleSubmit}>
              <h3>Request a Free Consultation</h3>
              <div className="con-form-row">
                <input name="name" type="text" placeholder="Full Name *" required value={formData.name} onChange={handleFormChange} />
                <input name="email" type="email" placeholder="Work Email *" required value={formData.email} onChange={handleFormChange} />
              </div>
              <div className="con-form-row">
                <input name="company" type="text" placeholder="Company Name *" required value={formData.company} onChange={handleFormChange} />
                <select name="size" value={formData.size} onChange={handleFormChange} required>
                  <option value="">Company Size *</option>
                  <option value="1-10">1–10 employees</option>
                  <option value="11-50">11–50 employees</option>
                  <option value="51-200">51–200 employees</option>
                  <option value="201-1000">201–1,000 employees</option>
                  <option value="1000+">1,000+ employees</option>
                </select>
              </div>
              <select name="service" value={formData.service} onChange={handleFormChange}>
                <option value="">What do you need help with?</option>
                <option value="ai-automation">AI & Automation</option>
                <option value="software-engineering">Software Engineering</option>
                <option value="data-analytics">Data & Analytics</option>
                <option value="crm-operations">CRM & Operations</option>
                <option value="staff-augmentation">Staff Augmentation</option>
                <option value="other">Other / Not Sure</option>
              </select>
              <textarea name="message" rows={4} placeholder="Tell us about your project or challenge…" value={formData.message} onChange={handleFormChange} />
              <button type="submit" className="con-btn-primary con-btn-full">
                <FiMessageSquare /> Request Consultation
              </button>
              <p className="con-form-note">We respond within 24 hours. No spam, ever.</p>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
